import React from 'react';
import { Users, Wrench, Brain, Award, Download } from 'lucide-react';
import { skills, certificates, techInnovations, projectStats } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const Expertise = () => {
  const skillCategories = [
    {
      title: 'Leadership & Management',
      icon: Users,
      skills: skills.leadership,
      color: 'bg-blue-50 text-blue-700'
    },
    {
      title: 'Technical Expertise',
      icon: Wrench,
      skills: skills.technical,
      color: 'bg-green-50 text-green-700'
    },
    {
      title: 'Personal Skills',
      icon: Brain,
      skills: skills.personal,
      color: 'bg-purple-50 text-purple-700'
    }
  ];

  const expertiseAreas = [
    {
      area: 'Project Management',
      level: 95,
      description: 'End-to-end project delivery from concept to completion'
    },
    {
      area: 'Technical Coordination',
      level: 90,
      description: 'Shop drawings, RFIs, and consultant coordination'
    },
    {
      area: 'Team Leadership',
      level: 92,
      description: 'Leading multidisciplinary teams across complex projects'
    },
    {
      area: 'Quality Control',
      level: 92,
      description: 'Ensuring international standards and client satisfaction'
    },
    {
      area: 'AutoCAD/Technical Drawing',
      level: 85,
      description: 'Advanced technical documentation and drawing production'
    },
    {
      area: 'AI & Process Automation',
      level: 88,
      description: 'Developing innovative solutions to streamline workflows'
    },
    {
      area: 'Python Programming',
      level: 75,
      description: 'Custom automation tools and data processing solutions'
    },
    {
      area: 'Risk Management',
      level: 87,
      description: 'Identifying and mitigating project risks proactively'
    }
  ];

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Professional Expertise</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            A decade of experience delivering complex architectural projects with precision, 
            innovation, and unwavering commitment to quality.
          </p>
        </div>
      </section>

      {/* Core Skills */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Core Competencies</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {skillCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-lg ${category.color} flex items-center justify-center mb-4`}>
                      <IconComponent size={24} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{category.title}</h3>
                    <div className="space-y-2">
                      {category.skills.map((skill, skillIndex) => (
                        <Badge key={skillIndex} variant="outline" className="mr-2 mb-2">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Expertise Levels */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Expertise Levels</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {expertiseAreas.map((expertise, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-lg text-gray-900">{expertise.area}</h3>
                  <span className="text-sm font-medium text-gray-600">{expertise.level}%</span>
                </div>
                <Progress value={expertise.level} className="mb-3" />
                <p className="text-sm text-gray-600">{expertise.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Certifications & Qualifications</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certificates.map((cert, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Award className="text-gray-600" size={32} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{cert.name}</h3>
                  <p className="text-sm text-gray-600 mb-1">{cert.issuer}</p>
                  <p className="text-sm font-medium text-gray-800">{cert.year}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Innovations */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Technology & Innovation</h2>
          {techInnovations.map((innovation, index) => (
            <Card key={index} className="mb-8 hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Wrench className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{innovation.title}</h3>
                        <p className="text-sm text-gray-500">{innovation.category} • {innovation.year}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-6 leading-relaxed">{innovation.description}</p>
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Key Features</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {innovation.features.map((feature, i) => (
                          <div key={i} className="flex items-start space-x-2 text-sm text-gray-600">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Impact & Results</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {innovation.impact.map((result, i) => (
                          <div key={i} className="flex items-start space-x-2 text-sm text-gray-600">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span>{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Technologies</h4>
                    <div className="space-y-3">
                      {innovation.technologies.map((tech, i) => (
                        <Badge key={i} variant="outline" className="mr-2 mb-2">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-900 mb-2">Project Status</div>
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {innovation.status}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Professional Approach */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Professional Approach</h2>
          <p className="text-lg leading-relaxed text-gray-300 mb-8">
            I believe that the role of the architect and technical office leader is not only to ensure 
            precision and efficiency but also to create an environment where collaboration, innovation, 
            and quality drive every stage of the project. My approach combines technical expertise with 
            strategic thinking to deliver results that exceed expectations.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-semibold mb-3">Quality First</h3>
              <p className="text-gray-300">Maintaining international standards while delivering innovative solutions</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">Team Collaboration</h3>
              <p className="text-gray-300">Building strong relationships across all project stakeholders</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">Continuous Learning</h3>
              <p className="text-gray-300">Staying current with industry trends and technological advances</p>
            </div>
          </div>
          <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-gray-900" asChild>
            <a href="/documents/Omar_Mosallam_CV.pdf" download="Omar_Mosallam_CV.pdf" target="_blank">
              <Download className="mr-2" size={16} />
              Download Resume
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Expertise;